import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Compass, Eye, Hand, Lightbulb, Target, ShieldCheck, Heart } from "lucide-react";

export default function About() {
  useEffect(() => { document.title = "About | Dutsen Kauna Foundation"; }, []);
  return (
    <div data-testid="about-page">
      <section className="relative bg-[#155e3f] text-white py-24 md:py-32 overflow-hidden">
        <img src="https://images.unsplash.com/photo-1565958923272-e96dbb1e8414?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwxfHxuaWdlcmlhJTIwdmlsbGFnZSUyMGxpZmV8ZW58MHx8fHwxNzgyNjA5NTUzfDA&ixlib=rb-4.1.0&q=85" alt="Community meeting under a tree during a Foundation needs assessment" loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover opacity-30" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#155e3f] via-[#155e3f]/85 to-[#155e3f]/40" />
        <div className="dk-container relative">
          <span className="text-[#d6aa43] tracking-[0.3em] text-xs font-semibold uppercase">About us</span>
          <h1 className="font-serif text-4xl md:text-6xl font-bold mt-3 max-w-3xl">A foundation built on Innovation, Impact, Integrity & Love.</h1>
          <p className="mt-6 text-white text-lg max-w-2xl">Dutsen Kauna Foundation exists to bring clean energy, education, empowerment and humanitarian support to underserved communities in Nigeria.</p>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="dk-container grid md:grid-cols-3 gap-6">
          {[
            { icon: Compass, title: "Mission", text: "Improve lives in underserved Nigerian communities through clean energy, education, economic empowerment and humanitarian support." },
            { icon: Eye, title: "Vision", text: "Reveal the unsearchable riches of Christ through ministry, love and transformative community action." },
            { icon: Hand, title: "What We Do", text: "Design and deliver sustainable, community-led solutions that address poverty, inequality and lack of opportunity." },
          ].map((c, i) => {
            const Icon = c.icon;
            return (
              <div key={c.title} className="p-8 rounded-2xl bg-[#f8f4e8] border border-[#d6aa43]/15" data-testid={`about-card-${i}`}>
                <div className="w-12 h-12 rounded-full bg-[#155e3f] text-[#d6aa43] flex items-center justify-center mb-5"><Icon size={22} /></div>
                <h3 className="font-serif text-2xl text-[#0a0d12] font-bold mb-3">{c.title}</h3>
                <p className="text-[#0a0d12]/75 leading-relaxed">{c.text}</p>
              </div>
            );
          })}
        </div>
      </section>

      <section className="py-20 bg-[#f8f4e8]">
        <div className="dk-container">
          <div className="max-w-2xl mb-12">
            <span className="text-[#0d1724] tracking-[0.3em] text-xs font-semibold uppercase">Our values</span>
            <h2 className="font-serif text-3xl md:text-5xl text-[#0a0d12] font-bold mt-3">The principles guiding every project.</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Lightbulb, title: "Innovation", text: "Creative, practical solutions to real community problems." },
              { icon: Target, title: "Impact", text: "Measurable transformation in lives and livelihoods." },
              { icon: ShieldCheck, title: "Integrity", text: "Transparency, accountability and stewardship." },
              { icon: Heart, title: "Love", text: "Compassion at the centre of all we do." },
            ].map((v, i) => {
              const Icon = v.icon;
              return (
                <div key={v.title} className="p-7 rounded-2xl bg-white border border-black/5 hover:-translate-y-1 hover:shadow-xl transition-all">
                  <div className="w-12 h-12 rounded-full bg-[#d6aa43]/10 text-[#d6aa43] flex items-center justify-center mb-5"><Icon size={22} /></div>
                  <h3 className="font-serif text-xl text-[#0a0d12] font-bold mb-2">{v.title}</h3>
                  <p className="text-sm text-[#0a0d12]/70 leading-relaxed">{v.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-20 bg-[#155e3f] text-white">
        <div className="dk-container text-center max-w-2xl mx-auto">
          <h2 className="font-serif text-3xl md:text-5xl font-bold mb-5">Walk with us.</h2>
          <p className="text-white mb-8">Donors, partners, volunteers and community leaders — together we can build measurable, lasting change.</p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Link to="/donate" className="dk-btn-primary">Donate <ArrowRight size={16} /></Link>
            <Link to="/contact" className="dk-btn-outline">Get in touch</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
